import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import {
  Phone,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Clock,
  RefreshCw,
  Star,
  Home,
  ShieldCheck,
  Building2,
  User,
  Mail,
  MapPin,
  Gift,
  Coins,
  Users,
} from "lucide-react";
import Notification, { showToast } from "../../components/common/notification/Notification";
import { loginSuccess } from "../../features/auth/authSlice";
import { getStoredAuthSession } from "./authSession";
import { getUserTypeName, isAdminUser } from "../../admin/navigation/adminNavigation";
import Logo from "../../assets/images/logo.png";
import DefaultHeroImg from "../../assets/images/hero.jpg";
import "./AuthForm.css";

const BASE_URL = (
  process.env.REACT_APP_BASE_URL || "https://services.allhomedesire.in"
).replace(/\/+$/, "");

const WhatsAppIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"
      fill="#25D366"
    />
    <path
      d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.122 1.532 5.857L0 24l6.306-1.508A11.95 11.95 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.797 9.797 0 01-5.003-1.373l-.36-.214-3.737.893.938-3.63-.235-.374A9.772 9.772 0 012.182 12C2.182 6.57 6.57 2.182 12 2.182S21.818 6.57 21.818 12 17.43 21.818 12 21.818z"
      fill="#25D366"
    />
  </svg>
);

export const AuthForm = ({
  type = "login",
  title = "Welcome",
  subtitle = "Login with your mobile number to continue",
  image = DefaultHeroImg,
  redirectOnSuccess,
  footerText = "Don’t have an account?",
  footerLink = "Sign Up",
  footerPath = "/sign-up",
  userType = "CUSTOMER",
}) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(120);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isValidatingOtp, setIsValidatingOtp] = useState(false);
  const [isProfileStep, setIsProfileStep] = useState(false);
  const [isBrandAmbassador, setIsBrandAmbassador] = useState(false);
  const [profileData, setProfileData] = useState({
    first_name: "",
    last_name: "",
    email_address: "",
    country_id: "",
    state_id: "",
    city_id: "",
  });
  const [locationValue, setLocationValue] = useState({
    country: "",
    state: "",
    city: "",
    area: "",
  });
  const [countriesList, setCountriesList] = useState([]);
  const [statesList, setStatesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [registeredUserId, setRegisteredUserId] = useState(null);
  const [registeredToken, setRegisteredToken] = useState(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const inputRefs = useRef([...Array(6)].map(() => React.createRef()));

  const resolveWorkspaceRedirect = (session) => {
    if (redirectOnSuccess) return redirectOnSuccess;
    const roleType = String(getUserTypeName(session) || userType || "").toUpperCase().trim();
    if (isAdminUser(session)) return "/admin";
    if (roleType === "PARTNER") return "/partner/workspace";
    return "/customer/workspace";
  };

  // Auto-redirect if already logged in with valid token (only if not on register flow)
  useEffect(() => {
    const savedAuth = getStoredAuthSession();
    if (savedAuth?.accessToken && type !== "register") {
      dispatch(loginSuccess(savedAuth));
      navigate(resolveWorkspaceRedirect(savedAuth), { replace: true });
    }
  }, [dispatch, navigate, type]);

  // Load countries when profile step is reached
  useEffect(() => {
    if (isProfileStep) {
      const fetchCountries = async () => {
        try {
          setIsLoadingLocation(true);
          const headers = registeredToken ? { Authorization: `Bearer ${registeredToken}` } : {};
          const res = await axios.post(
            `${BASE_URL}/admin/countries`,
            { page_number: 1, page_size: 100, order_by: "ASC" },
            { headers }
          );
          const list = res.data?.countries || [];
          setCountriesList(list);

          const india = list.find((c) => c.name?.toLowerCase() === "india") || list[0];
          if (india) {
            setLocationValue((prev) => ({ ...prev, country: String(india.id) }));
            setProfileData((prev) => ({ ...prev, country_id: String(india.id) }));
          }
        } catch (e) {
          console.error("Error fetching countries:", e);
        } finally {
          setIsLoadingLocation(false);
        }
      };
      fetchCountries();
    }
  }, [isProfileStep, registeredToken]);

  // Load states when country changes
  useEffect(() => {
    if (locationValue.country && isProfileStep) {
      const fetchStates = async () => {
        try {
          const headers = registeredToken ? { Authorization: `Bearer ${registeredToken}` } : {};
          const res = await axios.post(
            `${BASE_URL}/admin/states/${locationValue.country}`,
            { page_number: 1, page_size: 100, order_by: "ASC" },
            { headers }
          );
          const list = res.data?.states || [];
          setStatesList(list);
        } catch (e) {
          console.error("Error fetching states:", e);
        }
      };
      fetchStates();
    } else {
      setStatesList([]);
    }
  }, [locationValue.country, isProfileStep, registeredToken]);

  // Load cities when state changes
  useEffect(() => {
    if (locationValue.state && isProfileStep) {
      const fetchCities = async () => {
        try {
          const headers = registeredToken ? { Authorization: `Bearer ${registeredToken}` } : {};
          const res = await axios.post(
            `${BASE_URL}/admin/cities/${locationValue.state}`,
            { page_number: 1, page_size: 100, order_by: "ASC" },
            { headers }
          );
          const list = res.data?.cities || [];
          setCitiesList(list);
        } catch (e) {
          console.error("Error fetching cities:", e);
        }
      };
      fetchCities();
    } else {
      setCitiesList([]);
    }
  }, [locationValue.state, isProfileStep, registeredToken]);

  // Countdown timer for OTP resend
  useEffect(() => {
    let interval = null;
    if (otpSent && timer > 0 && !isProfileStep) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [otpSent, timer, isProfileStep]);

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 10) setPhoneNumber(value);
  };

  const handleSendOtp = async (e) => {
    if (e) e.preventDefault();
    if (phoneNumber.length !== 10) {
      showToast("Please enter a valid 10-digit mobile number.", { type: "error" });
      return;
    }

    setIsSendingOtp(true);
    try {
      const formattedMobile = "91" + phoneNumber;
      const response = await axios.post(`${BASE_URL}/all/otp/request`, {
        mobile: formattedMobile,
        action: type,
      });

      if (response?.data?.statusType === "SUCCESS" || response?.data?.status === "SUCCESS") {
        setOtpSent(true);
        setTimer(120);
        setOtp(["", "", "", "", "", ""]);
        showToast("OTP sent successfully to your mobile number!", { type: "success" });
        setTimeout(() => inputRefs.current[0]?.current?.focus(), 150);
      } else {
        const errorMsg = response?.data?.text || response?.data?.message || "Failed to send OTP.";
        showToast(errorMsg, { type: "error" });
      }
    } catch (error) {
      console.error("OTP send error:", error);
      const msg = error?.response?.data?.text || error?.response?.data?.message || "An error occurred while sending OTP.";
      showToast(msg, { type: "error" });
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleOtpChange = (e, idx) => {
    const val = e.target.value.replace(/\D/g, "");
    if (!val) return;

    const newOtp = [...otp];
    newOtp[idx] = val.slice(-1);
    setOtp(newOtp);

    if (idx < 5 && val) {
      inputRefs.current[idx + 1].current?.focus();
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const newOtp = [...otp];
      if (otp[idx]) {
        newOtp[idx] = "";
        setOtp(newOtp);
      } else if (idx > 0) {
        inputRefs.current[idx - 1].current?.focus();
        newOtp[idx - 1] = "";
        setOtp(newOtp);
      }
    } else if (e.key === "ArrowLeft" && idx > 0) {
      inputRefs.current[idx - 1].current?.focus();
    } else if (e.key === "ArrowRight" && idx < 5) {
      inputRefs.current[idx + 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasteData) {
      const newOtp = [...otp];
      for (let i = 0; i < 6; i++) {
        newOtp[i] = pasteData[i] || "";
      }
      setOtp(newOtp);
      const focusIndex = Math.min(pasteData.length, 5);
      inputRefs.current[focusIndex].current?.focus();
    }
  };

  const checkProfileCompletion = (userDetails, roleType = userType) => {
    if (!userDetails) return false;
    const isPartner = String(roleType).toUpperCase() === "PARTNER";
    const customerRequiredFields = ["first_name", "last_name", "email_address"];
    const requiredFields = isPartner
      ? [...customerRequiredFields, "service_id"]
      : customerRequiredFields;

    for (const field of requiredFields) {
      const val = userDetails[field];
      if (val === undefined || val === null || String(val).trim() === "") {
        return false;
      }
    }
    return true;
  };

  const handleValidateOtp = async (e) => {
    if (e) e.preventDefault();
    const otpValue = otp.join("");
    if (otpValue.length !== 6) {
      showToast("Please enter a valid 6-digit OTP code.", { type: "error" });
      return;
    }

    setIsValidatingOtp(true);
    try {
      const formattedMobile = "91" + phoneNumber;
      const response = await axios.post(`${BASE_URL}/all/otp/verify`, {
        mobile: formattedMobile,
        otp: otpValue,
        user_type: userType,
      });

      if (response?.data?.statusType === "SUCCESS" || response?.data?.status === "SUCCESS") {
        const sessionPayload = {
          statusType: response.data.statusType,
          accessToken: response.data.accessToken || response.data.token,
          token: response.data.token || response.data.accessToken,
          refreshToken: response.data.refreshToken,
          user: response.data.user,
        };

        dispatch(loginSuccess(sessionPayload));

        const uId = response.data.user?.id || sessionPayload.user?.id;
        const uToken = sessionPayload.refreshToken || sessionPayload.accessToken;
        setRegisteredUserId(uId);
        setRegisteredToken(uToken);

        const userObj = response.data.user || {};
        const isComplete = checkProfileCompletion(userObj, userType);

        if (String(userType).toUpperCase() === "PARTNER") {
          if (!isComplete) {
            showToast("Complete your partner profile to continue", { type: "info" });
            navigate("/partner/onboarding", { replace: true });
          } else {
            const subscriptionData =
              response.data.subscription && typeof response.data.subscription === "object"
                ? response.data.subscription
                : (response.data.subscription || null);
            const hasActiveSub = Boolean(subscriptionData && subscriptionData.status !== "EXPIRED");
            if (hasActiveSub) {
              showToast("Login successful!", { type: "success" });
              navigate("/partner/workspace", { replace: true });
            } else {
              navigate("/partner/subscription", { replace: true, state: { serviceId: userObj.service_id } });
            }
          }
          return;
        }

        if (type === "register" || !isComplete) {
          // Prepopulate existing fields
          setProfileData((prev) => ({
            ...prev,
            first_name: userObj.first_name || prev.first_name,
            last_name: userObj.last_name || prev.last_name,
            email_address: userObj.email_address || prev.email_address,
          }));

          if (type === "register") {
            showToast("Mobile verified successfully!", { type: "success" });
          } else {
            showToast("Complete your profile to continue", { type: "info" });
          }

          setIsProfileStep(true);
        } else {
          showToast("Login successful!", { type: "success" });
          navigate(resolveWorkspaceRedirect(sessionPayload), { replace: true });
        }
      } else {
        const errorMsg = response?.data?.text || response?.data?.message || "Invalid or expired OTP code.";
        showToast(errorMsg, { type: "error" });
      }
    } catch (error) {
      console.error("OTP validation error:", error);
      const msg = error?.response?.data?.text || error?.response?.data?.message || "An error occurred during OTP validation.";
      showToast(msg, { type: "error" });
    } finally {
      setIsValidatingOtp(false);
    }
  };

  const handleProfileFieldChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));
  };

  const handleStateSelect = (e) => {
    const val = e.target.value;
    setLocationValue((prev) => ({ ...prev, state: val, city: "" }));
    setProfileData((prev) => ({ ...prev, state_id: val, city_id: "" }));
  };

  const handleCitySelect = (e) => {
    const val = e.target.value;
    setLocationValue((prev) => ({ ...prev, city: val }));
    setProfileData((prev) => ({ ...prev, city_id: val }));
  };

  const handleSkipProfile = () => {
    const stored = getStoredAuthSession();
    navigate(resolveWorkspaceRedirect(stored), { replace: true });
  };

  const handleSaveProfile = async (e) => {
    if (e) e.preventDefault();

    if (!profileData.first_name?.trim()) {
      showToast("Please enter your first name.", { type: "error" });
      return;
    }

    if (!profileData.last_name?.trim()) {
      showToast("Please enter your last name.", { type: "error" });
      return;
    }

    if (!profileData.email_address?.trim()) {
      showToast("Please enter your email address.", { type: "error" });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(profileData.email_address.trim())) {
      showToast("Please enter a valid email address.", { type: "error" });
      return;
    }

    setIsSavingProfile(true);
    try {
      const selectedStateObj = statesList.find(
        (s) => String(s.id) === String(locationValue.state || profileData.state_id)
      );
      const selectedCityObj = citiesList.find(
        (c) => String(c.id) === String(locationValue.city || profileData.city_id)
      );

      const payload = {
        id: registeredUserId,
        first_name: profileData.first_name.trim(),
        last_name: profileData.last_name.trim(),
        email_address: profileData.email_address.trim(),
        mobile_number: "91" + phoneNumber,
        whatsapp_mobile_number: "91" + phoneNumber,
        country_id: profileData.country_id || locationValue.country || "",
        state_id: profileData.state_id || locationValue.state || "",
        city_id: profileData.city_id || locationValue.city || "",
        state: selectedStateObj?.name || "",
        city: selectedCityObj?.name || "",
        is_brand_ambassador: isBrandAmbassador,
      };

      const headers = {
        "Content-Type": "application/json",
        ...(registeredToken ? { Authorization: `Bearer ${registeredToken}` } : {}),
      };

      let response = null;
      try {
        response = await axios.put(`${BASE_URL}/admin/editProfile`, payload, { headers });
      } catch (errProfile) {
        try {
          response = await axios.put(`${BASE_URL}/admin/editUser`, payload, { headers });
        } catch (errUser) {
          response = await axios.post(`${BASE_URL}/admin/editUser`, payload, { headers });
        }
      }

      if (response?.data?.statusType === "SUCCESS" || response?.data?.status === "SUCCESS") {
        showToast("Profile completed successfully!", { type: "success" });

        // Optional welcome customer trigger matching mobile
        try {
          await axios.post(`${BASE_URL}/all/send-welcome/customer`, null, {
            params: {
              toPhone: "91" + phoneNumber,
              name: payload.first_name,
            },
          });
        } catch (ignoreErr) {
          // ignore welcome message background error
        }

        // Update local session user object
        const stored = getStoredAuthSession();
        const updatedUser = {
          ...(stored?.user || {}),
          id: registeredUserId || stored?.user?.id,
          first_name: payload.first_name,
          last_name: payload.last_name,
          name: `${payload.first_name} ${payload.last_name}`.trim(),
          email_address: payload.email_address,
          email: payload.email_address,
          mobile_number: payload.mobile_number,
          mobile: payload.mobile_number,
          is_brand_ambassador: isBrandAmbassador,
          cities: selectedCityObj
            ? [
                {
                  id: selectedCityObj.id,
                  name: selectedCityObj.name,
                  state: selectedStateObj
                    ? { id: selectedStateObj.id, name: selectedStateObj.name }
                    : undefined,
                },
              ]
            : stored?.user?.cities,
        };

        const updatedSession = {
          ...(stored || {}),
          user: updatedUser,
          userDetails: updatedUser,
          first_name: payload.first_name,
          last_name: payload.last_name,
          name: `${payload.first_name} ${payload.last_name}`.trim(),
        };

        dispatch(loginSuccess(updatedSession));
        navigate(resolveWorkspaceRedirect(updatedSession), { replace: true });
      } else {
        showToast(response?.data?.text || "Profile updated", { type: "info" });
        const stored = getStoredAuthSession();
        navigate(resolveWorkspaceRedirect(stored), { replace: true });
      }
    } catch (err) {
      console.error("Profile save error:", err);
      showToast("Profile saved. Redirecting to workspace...", { type: "info" });
      const stored = getStoredAuthSession();
      navigate(resolveWorkspaceRedirect(stored), { replace: true });
    } finally {
      setIsSavingProfile(false);
    }
  };

  const formatTimer = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  const bgImage = image || DefaultHeroImg;

  return (
    <div className="auth-split-layout">
      <Notification />

      {/* LEFT VISUAL PANEL (50%) */}
      <div className="auth-split__left">
        <div
          className="auth-split__left-bg"
          style={{ backgroundImage: `url(${bgImage})` }}
        />
        <div className="auth-split__left-overlay" />

        <div className="auth-split__left-content">
          <div className="auth-visual-card">
            {/* Top Glass Trust Badge */}
            <div className="auth-visual-card__badge">
              <span className="auth-visual-card__badge-dot" />
              <span>10k+ Verified Properties</span>
            </div>

            <div className="auth-visual-card__brand">ALL HOME DESIRE</div>

            <h2 className="auth-visual-card__title">
              Find Your <span className="auth-visual-card__highlight">Perfect Home</span> Today
            </h2>

            <p className="auth-visual-card__subtitle">
              "Thousands of verified properties across India — transparent, effortless, and tailored to you."
            </p>

            <div className="auth-visual-card__highlights">
              <div className="auth-visual-highlight">
                <div className="auth-visual-highlight__icon">
                  <Home size={18} />
                </div>
                <div className="auth-visual-highlight__text">
                  <h4>100% Verified Listings</h4>
                  <p>Directly verified flats, villas, plots & commercial spaces</p>
                </div>
              </div>

              <div className="auth-visual-highlight">
                <div className="auth-visual-highlight__icon">
                  <ShieldCheck size={18} />
                </div>
                <div className="auth-visual-highlight__text">
                  <h4>Zero Brokerage & Direct Connect</h4>
                  <p>Transparent property deals with verified owners and builders</p>
                </div>
              </div>

              <div className="auth-visual-highlight">
                <div className="auth-visual-highlight__icon">
                  <Building2 size={18} />
                </div>
                <div className="auth-visual-highlight__text">
                  <h4>Complete Home Ecosystem</h4>
                  <p>Access interior designers, contractors, and local home services</p>
                </div>
              </div>
            </div>

            {/* Social proof rating */}
            <div className="auth-visual-social">
              <div className="auth-visual-social__stars">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14} fill="#f59e0b" color="#f59e0b" />
                ))}
                <span className="auth-visual-social__score">4.9 / 5</span>
              </div>
              <p className="auth-visual-social__text">
                Trusted by 100,000+ happy homeowners & property seekers across India
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT FORM PANEL (50%) */}
      <div className="auth-split__right">
        <div className="auth-split__right-content">
          {/* Header Branding */}
          <div className="auth-split__header">
            <Link to="/" className="auth-split__logo-link">
              <img src={Logo} alt="All Home Desire" className="auth-split__logo" />
            </Link>
            <div className="auth-split__header-right">
              <Link to="/" className="auth-back-home-link" title="Back to Home">
                <ArrowLeft size={14} />
                <span>Back to Home</span>
              </Link>
              <span className="auth-split__badge">
                {type === "register" ? "NEW ACCOUNT" : "CUSTOMER PORTAL"}
              </span>
            </div>
          </div>

          {/* STATE 1: MOBILE NUMBER ENTRY */}
          {!otpSent ? (
            <div className="auth-step-pane">
              <div className="auth-split__titles">
                <h1 className="auth-split__title">{title}</h1>
                <p className="auth-split__subtitle">{subtitle}</p>
              </div>

              <form onSubmit={handleSendOtp} className="auth-form-body">
                <div className="auth-form__group">
                  <label className="auth-form__label" htmlFor="customer-mobile">
                    Mobile Number <span className="auth-required">*</span>
                  </label>
                  <div className="auth-input-wrap">
                    <span className="auth-input-icon">
                      <Phone size={18} />
                    </span>
                    <span className="auth-input-divider" />
                    <span className="auth-input-prefix">+91</span>
                    <input
                      id="customer-mobile"
                      type="tel"
                      inputMode="numeric"
                      placeholder="Enter 10 digit number"
                      className="auth-input"
                      value={phoneNumber}
                      onChange={handlePhoneChange}
                      maxLength={10}
                      autoFocus
                    />
                  </div>
                  <div className="auth-whatsapp-note">
                    <WhatsAppIcon />
                    <span>OTP will be sent to your WhatsApp number</span>
                  </div>
                </div>

                <button
                  type="submit"
                  className="auth-btn auth-btn--primary auth-btn--lg"
                  disabled={phoneNumber.length !== 10 || isSendingOtp}
                >
                  {isSendingOtp ? (
                    <span className="auth-spinner">Sending OTP...</span>
                  ) : (
                    <>
                      <span>Send OTP</span>
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>

                {footerText && footerLink && (
                  <div className="auth-switch">
                    <span>{footerText}</span>
                    <Link to={footerPath} className="auth-switch__link">
                      {footerLink}
                    </Link>
                  </div>
                )}
              </form>
            </div>
          ) : !isProfileStep ? (
            /* STATE 2: 6-DIGIT OTP VERIFICATION */
            <div className="auth-step-pane">
              <div className="auth-split__titles">
                <h1 className="auth-split__title">Verify your number</h1>
                <p className="auth-split__subtitle">
                  We've sent a 6-digit verification code to +91 {phoneNumber}
                </p>
              </div>

              <div className="auth-verify-target-row">
                <div className="auth-verify-target-badge">
                  <span>+91 {phoneNumber}</span>
                </div>
                <button
                  type="button"
                  className="auth-verify-change-btn"
                  onClick={() => {
                    setOtpSent(false);
                    setOtp(["", "", "", "", "", ""]);
                  }}
                >
                  Change number
                </button>
              </div>

              <form onSubmit={handleValidateOtp} className="auth-form-body">
                <div className="auth-form__group text-center">
                  <label className="auth-form__label text-center mb-2">
                    Enter 6-digit OTP code
                  </label>
                  <div className="auth-otp-grid" onPaste={handlePaste}>
                    {otp.map((digit, idx) => (
                      <input
                        key={idx}
                        ref={inputRefs.current[idx]}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        className={`auth-otp-input ${digit ? "auth-otp-input--filled" : ""}`}
                        onChange={(e) => handleOtpChange(e, idx)}
                        onKeyDown={(e) => handleKeyDown(e, idx)}
                        autoFocus={idx === 0}
                      />
                    ))}
                  </div>
                </div>

                <div className="auth-timer-section">
                  {timer > 0 ? (
                    <div className="auth-timer-badge">
                      <Clock size={15} />
                      <span>Code expires in {formatTimer(timer)}</span>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="auth-resend-btn"
                      onClick={handleSendOtp}
                      disabled={isSendingOtp}
                    >
                      <RefreshCw size={14} className={isSendingOtp ? "auth-spin" : ""} />
                      <span>{isSendingOtp ? "Resending code..." : "Resend OTP Code"}</span>
                    </button>
                  )}
                </div>

                <button
                  type="submit"
                  className="auth-btn auth-btn--primary auth-btn--lg"
                  disabled={otp.join("").length !== 6 || isValidatingOtp}
                >
                  {isValidatingOtp ? (
                    <span className="auth-spinner">Validating OTP...</span>
                  ) : (
                    <>
                      <span>Verify & Continue</span>
                      <CheckCircle2 size={18} />
                    </>
                  )}
                </button>

                <div className="auth-switch">
                  <button
                    type="button"
                    className="auth-back-link"
                    onClick={() => {
                      setOtpSent(false);
                      setOtp(["", "", "", "", "", ""]);
                    }}
                  >
                    <ArrowLeft size={16} />
                    <span>Back to mobile entry</span>
                  </button>
                </div>
              </form>
            </div>
          ) : (
            /* STATE 3: COMPLETE YOUR PROFILE (SAME PAGE) */
            <div className="auth-step-pane">
              <div className="auth-split__titles">
                <h1 className="auth-split__title">Complete Your Profile</h1>
                <p className="auth-split__subtitle">
                  Please provide your details to finish setting up your account
                </p>
              </div>

              <form onSubmit={handleSaveProfile} className="auth-form-body">
                <div className="auth-grid-2col">
                  <div className="auth-form__group">
                    <label className="auth-form__label" htmlFor="cust-first-name">
                      First Name <span className="auth-required">*</span>
                    </label>
                    <div className="auth-input-wrap">
                      <span className="auth-input-icon">
                        <User size={18} />
                      </span>
                      <input
                        id="cust-first-name"
                        type="text"
                        name="first_name"
                        className="auth-input"
                        placeholder="Enter first name"
                        value={profileData.first_name}
                        onChange={handleProfileFieldChange}
                        required
                        autoFocus
                      />
                    </div>
                  </div>

                  <div className="auth-form__group">
                    <label className="auth-form__label" htmlFor="cust-last-name">
                      Last Name <span className="auth-required">*</span>
                    </label>
                    <div className="auth-input-wrap">
                      <span className="auth-input-icon">
                        <User size={18} />
                      </span>
                      <input
                        id="cust-last-name"
                        type="text"
                        name="last_name"
                        className="auth-input"
                        placeholder="Enter last name"
                        value={profileData.last_name}
                        onChange={handleProfileFieldChange}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="auth-form__group">
                  <label className="auth-form__label" htmlFor="cust-phone-display">
                    WhatsApp Number
                  </label>
                  <div className="auth-input-wrap" style={{ backgroundColor: "#f1f5f9" }}>
                    <span className="auth-input-icon">
                      <WhatsAppIcon />
                    </span>
                    <input
                      id="cust-phone-display"
                      type="text"
                      className="auth-input"
                      value={`+91 ${phoneNumber}`}
                      disabled
                      style={{ cursor: "not-allowed", color: "#475569" }}
                    />
                    <CheckCircle2 size={16} color="#16a34a" style={{ marginRight: "12px" }} />
                  </div>
                </div>

                <div className="auth-form__group">
                  <label className="auth-form__label" htmlFor="cust-email">
                    Email Address <span className="auth-required">*</span>
                  </label>
                  <div className="auth-input-wrap">
                    <span className="auth-input-icon">
                      <Mail size={18} />
                    </span>
                    <input
                      id="cust-email"
                      type="email"
                      name="email_address"
                      className="auth-input"
                      placeholder="name@example.com"
                      value={profileData.email_address}
                      onChange={handleProfileFieldChange}
                      required
                    />
                  </div>
                </div>

                <div className="auth-grid-2col">
                  <div className="auth-form__group">
                    <label className="auth-form__label">
                      State <span className="auth-required">*</span>
                    </label>
                    <select
                      className="auth-styled-select"
                      value={locationValue.state}
                      onChange={handleStateSelect}
                      disabled={isLoadingLocation || statesList.length === 0}
                    >
                      <option value="">{isLoadingLocation ? "Loading states..." : "Select State"}</option>
                      {statesList.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="auth-form__group">
                    <label className="auth-form__label">
                      City <span className="auth-required">*</span>
                    </label>
                    <select
                      className="auth-styled-select"
                      value={locationValue.city}
                      onChange={handleCitySelect}
                      disabled={!locationValue.state || citiesList.length === 0}
                    >
                      <option value="">Select City</option>
                      {citiesList.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Brand Ambassador Section (Customer specific, matching mobile) */}
                {userType === "CUSTOMER" && (
                  <div className="auth-ambassador-section">
                    <label className="auth-checkbox-row">
                      <input
                        type="checkbox"
                        checked={isBrandAmbassador}
                        onChange={(e) => setIsBrandAmbassador(e.target.checked)}
                      />
                      <span>Become a Brand Ambassador</span>
                    </label>

                    <div className="auth-benefits-card">
                      <div className="auth-benefits-title">
                        Brand Ambassador Benefits
                      </div>
                      <div className="auth-benefit-item">
                        <Coins size={15} />
                        <span>Get ₹10,000 for every property sale generated through your referral.</span>
                      </div>
                      <div className="auth-benefit-item">
                        <Users size={15} />
                        <span>Refer property buyers and sellers to unlock perks.</span>
                      </div>
                      <div className="auth-benefit-item">
                        <Gift size={15} />
                        <span>Earn additional rewards and exclusive platform incentives.</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="auth-profile-actions">
                  <button
                    type="button"
                    className="auth-btn-skip"
                    onClick={handleSkipProfile}
                  >
                    Skip for now
                  </button>
                  <button
                    type="submit"
                    className="auth-btn auth-btn--primary auth-btn-save"
                    disabled={isSavingProfile}
                  >
                    {isSavingProfile ? (
                      <span className="auth-spinner">Saving Profile...</span>
                    ) : (
                      <>
                        <span>Complete Profile</span>
                        <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Footer note */}
          <div className="auth-split__footer">
            <span>© {new Date().getFullYear()} All Home Desire. All rights reserved.</span>
            <div className="auth-split__footer-links">
              <Link to="/terms-conditions">Terms</Link>
              <span>•</span>
              <Link to="/privacy-policy">Privacy</Link>
              <span>•</span>
              <Link to="/contact">Support</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthForm;
